import { PAGES } from "../context/PageContext"

const DIALOG_CLOSED_BY_USER = 12006

export class DialogDismissedError extends Error {
    constructor() {
        super("The sign-in window was closed before sign-in finished.")
        this.name = "DialogDismissedError"
    }
}

interface OpenDialogRequest {
    page: PAGES
    dialogOptions: Office.DialogOptions
    additionalParams: Record<string, string>
}

function buildDialogUrl(page: PAGES, additionalParams: Record<string, string>): string {
    const url = new URL(window.location.href)
    url.searchParams.set("page", page)
    Object.entries(additionalParams).forEach(([key, value]) => {
        url.searchParams.set(key, value)
    })
    return url.toString()
}

function parseDialogPayload(message: string): unknown {
    try {
        return JSON.parse(message)
    } catch {
        return message
    }
}

const openDialog = ({ page, dialogOptions, additionalParams }: OpenDialogRequest): Promise<unknown> =>
    new Promise((resolve, reject) => {
        Office.context.ui.displayDialogAsync(
            buildDialogUrl(page, additionalParams),
            dialogOptions,
            (result) => {
                if (result.status !== Office.AsyncResultStatus.Succeeded) {
                    reject(new Error(`The sign-in window could not open: ${result.error.message}`))
                    return
                }

                const dialog = result.value

                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args) => {
                    dialog.close()
                    if ("error" in args) {
                        reject(new Error(`The sign-in window reported error ${args.error}.`))
                        return
                    }
                    resolve(parseDialogPayload(args.message))
                })

                dialog.addEventHandler(Office.EventType.DialogEventReceived, (args) => {
                    if (!("error" in args)) return
                    if (args.error === DIALOG_CLOSED_BY_USER) {
                        reject(new DialogDismissedError())
                        return
                    }
                    reject(new Error(`The sign-in window closed unexpectedly (error ${args.error}).`))
                })
            }
        )
    })

export default openDialog
